/**
 * 2nd Operation — consolidates two legacy SFTS pages into one report: 2nd Operation Summary
 * (`sftsRpt/2nd/2ndOperationSummaryReport_v2`), 2nd Operation Detail
 * (`sftsRpt/2nd/2ndOperationDetailReport_v2`).
 *
 * Summary and detail read one shared fact view, so a summary row and the rows a reader drills into
 * cannot disagree about which scans happened.
 *
 * Legacy refused to run any of these screens on arrival until a style, MO or part was typed in.
 * That gate is not kept: factory and a date range are both required here, so the query is already
 * bounded — the same reasoning the Spreading port recorded for its detail screen.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ExportMenu } from '@/components/report/ExportMenu';
import { exportColumns, groupedColumns, integer } from '@/components/report/groupedColumns';
import { useReportFilters } from '@/components/report/useReportFilters';
import { Button } from '@/components/ui/button';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';
import { cn } from '@/lib/utils';

import { hasLegacyParams, translateDeepLink } from './deepLink';
import { secondOperationFilters, type SecondOperationView } from './filters';

const REPORT_ID = 'second-operation';

/** Summary first, then its finer aggregate — coarse to fine. */
const TABS: ReadonlyArray<readonly [SecondOperationView, string]> = [
  ['summary', 'Summary'],
  ['detail', 'Detail'],
];

/**
 * The fields identifying the approved Summary row. The hidden `pro_group` code travels with the
 * visible name so the Detail drill cannot broaden across two codes with the same label.
 */
const DRILL_FIELDS = [
  'factory',
  'b_no',
  'batch_no',
  'set_group',
  'partnos',
  'pro_group',
  'from_dept',
  'to_dept',
] as const;

/**
 * `wip` is a balance, but it is a difference of the sums either side of it, so summing it down the
 * column is the same arithmetic and the total holds. Nothing on this report repeats an order-level
 * figure across rows, so no measure has to be taken back out of the totals row.
 */
function isView(value: string | null): value is SecondOperationView {
  return value === 'summary' || value === 'detail';
}

export default function SecondOperationReport() {
  const { user, status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);

  const requestedView = searchParams.get('view');
  const view: SecondOperationView = isView(requestedView) ? requestedView : 'summary';

  const schema = useMemo(() => secondOperationFilters(factory, view), [factory, view]);
  const { values, isComplete, setValue } = useReportFilters(schema);

  // Win Master links land here with the old parameter names. Translated once, replacing the entry
  // so Back does not bounce through the untranslated address — see deepLink.ts.
  const legacyLink = useMemo(
    () => (hasLegacyParams(searchParams) ? translateDeepLink(searchParams) : null),
    [searchParams],
  );
  // The note outlives the translation: the legacy parameters are gone from the URL a render later,
  // and what could not be honoured has to stay on screen.
  const [deepLinkNotice] = useState<string | null>(() => legacyLink?.unsupported ?? null);
  useEffect(() => {
    if (legacyLink === null) return;
    setSearchParams(legacyLink.params, { replace: true });
  }, [legacyLink, setSearchParams]);

  // Write the factory into the URL, or a shared link resolves to the *recipient's* factory — the
  // same address showing two people different numbers.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  const setUrlParam = useCallback(
    (name: string, value: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * One filter set per view, built from the view's own schema. `report.yaml` scopes several
   * filters with `views:`, and sending one to a view that does not declare it is a 400 rather than
   * an ignored key — so the request carries exactly what this view's schema offered.
   */
  const filters = useMemo<ReportQueryRequest['filters']>(() => {
    const request: Record<string, string | null> = {};
    for (const field of schema) {
      if (field.type === 'date-range') {
        request[field.fromParam] = values[field.fromParam] ?? null;
        request[field.toParam] = values[field.toParam] ?? null;
      } else {
        request[field.name] = values[field.name] ?? null;
      }
    }
    // Preserve the hidden routing-group constraint used by exact Summary-row drill-through.
    request.pro_group = searchParams.get('pro_group');
    return request;
  }, [schema, values, searchParams]);

  const active = useReportQuery<Record<string, unknown>>(REPORT_ID, filters, {
    view,
    enabled: isComplete,
  });

  const rows = useMemo(
    () => (active.data?.rows ?? []) as Record<string, unknown>[],
    [active.data],
  );

  /**
   * Summary → Detail is an in-page switch at the selected row's day and complete Summary grain.
   * The `ALL` check remains for old deep links; redesigned Summary rows carry actual departments.
   */
  const openInDetail = useCallback(
    (row: Record<string, unknown>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('view', 'detail');
          const operationDate = row.op_date;
          if (operationDate !== null && operationDate !== undefined) {
            next.set('from', String(operationDate));
            next.set('to', String(operationDate));
          }
          for (const field of DRILL_FIELDS) {
            const value = row[field];
            const text = value === null || value === undefined ? '' : String(value);
            if (text === '' || text === 'ALL') next.delete(field);
            else next.set(field, text);
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const visibleColumnDescriptors = useMemo(
    () =>
      (active.data?.columns ?? []).filter(
        (column) => view !== 'summary' || column.field !== 'pro_group',
      ),
    [view, active.data],
  );

  const columns = useMemo(() => {
    const built = groupedColumns<Record<string, unknown>>(visibleColumnDescriptors);
    if (view !== 'summary') return built;
    const drill: ColumnDef<Record<string, unknown>> = {
      id: 'open',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="link"
          size="xs"
          // Underlined at rest, not only on hover: it is the one cell on the row that does
          // something, and a link that only looks like one under the pointer is not discoverable.
          className="px-1 underline"
          aria-label="Open this group in Detail"
          onClick={() => {
            openInDetail(row.original);
          }}
        >
          Scans
        </Button>
      ),
    };
    return [drill, ...built];
  }, [view, visibleColumnDescriptors, openInDetail]);

  // Only over a complete result (§4.3): a total over a capped one presents part as the whole.
  // Legacy totalled the whole filtered set while showing one page of it; this shows the same
  // number when the result is complete and no number at all when it is not.
  const totals =
    active.data !== undefined && !active.data.truncated
      ? {
          labelColumn: 'factory',
          label: 'Total',
          format: (value: number) => integer.format(Math.round(value)),
        }
      : undefined;

  const viewTabs = (
    <div role="tablist" aria-label="View" className="flex gap-0.5 border-b border-border">
      {TABS.map(([id, label]) => (
        <Button
          key={id}
          role="tab"
          variant="ghost"
          aria-selected={view === id}
          onClick={() => {
            setUrlParam('view', id);
          }}
          className={cn(
            'h-8 rounded-none border-b-2 border-transparent px-3 text-sm',
            view === id && 'border-primary font-medium text-foreground',
          )}
        >
          {label}
        </Button>
      ))}
    </div>
  );

  const caption =
    view === 'summary'
      ? 'Daily bundle movement by department route, part and sub-operation name'
      : 'Daily bundle movement by location, size, body, bed and user';

  const table = !isComplete ? (
    <DataTable
      columns={[]}
      data={[]}
      emptyMessage="Choose an operation date range and a factory to run the report."
    />
  ) : (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={active.isLoading}
      virtualized
      totals={totals}
      truncated={active.data?.truncated === true}
      caption={caption}
      emptyMessage="No second-operation movement for these filters. Widen the operation date range, or clear a filter."
    />
  );

  return (
    <ReportPage
      title="2nd Operation"
      description={
        deepLinkNotice
        ?? 'What moved through the second-operation departments — summarized by day or in finer detail.'
      }
      filters={
        <FilterBar
          schema={schema}
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={{ view, filters }}
              columns={exportColumns(columns)}
              disabled={!isComplete}
            />
          }
        />
      }
      views={viewTabs}
      table={table}
      isLoading={active.isLoading}
      error={active.error}
      generatedAt={active.data?.generatedAt ?? null}
      dataAsOf={active.data?.dataAsOf ?? null}
      truncated={active.data?.truncated ?? false}
    />
  );
}
